import { BgOpeningHours, AvailabilityEnum, Maybe } from "../../__generated__/graphql";
import { ShipAvailability, ShipAvailabilityException } from "../ships/types/ship";
import { Moment } from "moment";

import { shortid } from "imports/Utils";

import "./styling/controltower.scss";
import moment from "moment/moment";

type OpeningHours = BgOpeningHours & {
	[key: string]: Maybe<number> | string | number | undefined;
};

type GroupEntity = {
	workingHours?: OpeningHours;
	availability: ShipAvailability;
	id: number;
}

type TimelineUnavailabilityItem = {
	id: string;
	group: number;
	start: Moment;
	end: Moment;
	className: string;
};

type ShipTimelineItem = {
	ship: GroupEntity;
	id: string;
}

const isDayAvailable = (
	availability: ShipAvailability,
	date: Moment,
): boolean => {

	if (availability.defaultAvailability === false) {
		return false;
	}

	const day = moment(date).day();

	if (day === 6) {
		return availability.availableSaturday !== false;
	}

	if (day === 0) {
		return availability.availableSunday !== false;
	}

	return true;
};

const getTimeWindowUnavailability = (
	groupEntity: GroupEntity,
	targetDate: Moment,
): TimelineUnavailabilityItem[] => {

	const { workingHours, availability } = groupEntity;
	const unavailableBlocks: TimelineUnavailabilityItem[] = [];

	const dayName = targetDate.format("dddd");
	const dayKey = dayName.toLowerCase();

	const fromKey = `${dayKey}From`;
	const toKey = `${dayKey}To`;

	const fromMinutes = workingHours
		? (workingHours[fromKey] as number | null)
		: null;

	const toMinutes = workingHours
		? (workingHours[toKey] as number | null)
		: null;

	const hasWorkingHours =
		fromMinutes != null &&
		toMinutes != null &&
		fromMinutes < toMinutes;

	const dayStart = targetDate.clone().startOf("day");
	const dayEnd = targetDate.clone().endOf("day").add(1, "ms");

	if (hasWorkingHours) {
		const workStart = targetDate.clone().startOf("day").add(fromMinutes, "minutes");
		const workEnd = targetDate.clone().startOf("day").add(toMinutes, "minutes");

		if (workStart.isAfter(dayStart)) {
			unavailableBlocks.push({
				id: shortid.generate(),
				group: groupEntity.id,
				start: dayStart,
				end: workStart,
				className: "ship-unavailability",
			});
		}

		if (workEnd.isBefore(dayEnd)) {
			unavailableBlocks.push({
				id: shortid.generate(),
				group: groupEntity.id,
				start: workEnd,
				end: dayEnd,
				className: "ship-unavailability",
			});
		}

		return unavailableBlocks;
	}

	switch (availability.availableDayNight) {
		case AvailabilityEnum.Day:
			unavailableBlocks.push({
				id: shortid.generate(),
				group: groupEntity.id,
				start: dayStart,
				end: targetDate.clone().hour(6).minute(0).second(0),
				className: "ship-unavailability",
			});
			return unavailableBlocks;

		case AvailabilityEnum.Night:
			unavailableBlocks.push({
				id: shortid.generate(),
				group: groupEntity.id,
				start: dayStart,
				end: targetDate.clone().hour(12).minute(0).second(0),
				className: "ship-unavailability",
			});
			return unavailableBlocks;

		case AvailabilityEnum.DayAndNight:
		default:
			return [];
	}
};

const generateUnavailableBlocksForDay = (
	groupEntity: GroupEntity,
	targetDate: Moment,
): TimelineUnavailabilityItem[] => {

	const { availability } = groupEntity;

	const dayStart = targetDate.clone().startOf("day");
	const dayEnd = targetDate.clone().endOf("day").add(1, "ms");

	if (!isDayAvailable(availability, targetDate)) {
		return [ {
			id: shortid.generate(),
			group: groupEntity.id,
			start: dayStart,
			end: dayEnd,
			className: "ship-unavailability",
		} ];
	}

	return getTimeWindowUnavailability(groupEntity, targetDate);
};

export const generateDailyUnavailableBlocks = (
	item: ShipTimelineItem,
): TimelineUnavailabilityItem[] => {

	const items: TimelineUnavailabilityItem[] = [];
	const today = moment().startOf("day");
	const futureBoundary = today.clone().add(90, "days").endOf("day");

	if (item.ship && (item.ship.workingHours || item.ship.availability)) {
		const date = today.clone();

		const visibleDaysCount = futureBoundary.diff(today, "days");

		for (let i = 0; i <= visibleDaysCount; i++) {
			const dailyBlocks = generateUnavailableBlocksForDay(
				item.ship,
				date,
			);

			items.push(...dailyBlocks);

			date.add(1, "day");
		}
	}

	return items;
};

export const filterUnavailableByExceptions = (
	unavailabilityItems: TimelineUnavailabilityItem[],
	exceptions: ShipAvailabilityException[],
): TimelineUnavailabilityItem[] => {

	const exceptionRanges = exceptions.map(ex => ({
		startDay: moment(ex.startDate).startOf("day"),
		endDay: moment(ex.endDate).endOf("day"),
	}));


	return unavailabilityItems.filter(block => {
		const blockDay = moment(block.start).startOf("day");

		const isCovered = exceptionRanges.some(ex =>
			blockDay.isSameOrAfter(ex.startDay) &&
			blockDay.isSameOrBefore(ex.endDay),
		);

		return !isCovered;
	});
};
